import { useCallback, useEffect, useRef, type RefObject } from 'react';
import { onBeforeDrawerInsetChange } from './drawerInset';

/**
 * Запомненная позиция живёт один кадр: смена ширины и перекладка сетки приходят
 * до отрисовки. Дольше — раскладка не менялась (мобильный), запись устарела.
 */
const ANCHOR_TTL_MS = 250;

interface Anchor {
  id: string;
  top: number;
  at: number;
}

interface Settled {
  id: string;
  top: number;
  scrollY: number;
}

/**
 * Высота карточки на экране по раскладке, без трансформаций: переложенная
 * карточка Natural ещё едет в анимации появления, нажатая — сжата, и
 * `getBoundingClientRect` вернул бы сдвинутое значение.
 */
function viewportTop(element: HTMLElement): number {
  let top = 0;
  for (let node: HTMLElement | null = element; node; node = node.offsetParent as HTMLElement | null)
    top += node.offsetTop;
  return top - window.scrollY;
}

function isOnScreen(element: HTMLElement): boolean {
  const top = viewportTop(element);
  return top + element.offsetHeight > 0 && top < window.innerHeight;
}

/** Карточка открытого видео, если она на экране; иначе первая видимая. */
function pickAnchor(container: HTMLElement, preferredId: number | null): HTMLElement | null {
  if (preferredId !== null) {
    const preferred = container.querySelector<HTMLElement>(`[data-video-id="${preferredId}"]`);
    if (preferred && isOnScreen(preferred)) return preferred;
  }
  for (const card of container.querySelectorAll<HTMLElement>('[data-video-id]')) {
    if (isOnScreen(card)) return card;
  }
  return null;
}

/**
 * Панель разбора сужает страницу, сетка перекладывается, и карточки уезжают по
 * вертикали. Хук держит на месте карточку, по которой кликнули: позиция
 * запоминается до смены ширины, возвращаемая функция докручивает страницу после
 * перекладки. Карточки ищутся по `data-video-id`.
 */
export function useReflowAnchor(
  containerRef: RefObject<HTMLElement | null>,
  openVideoId: number | null,
): () => void {
  // Закрытие панели тоже перекладывает сетку — опорой остаётся последнее открытое видео.
  const lastOpenId = useRef<number | null>(null);
  if (openVideoId !== null) lastOpenId.current = openVideoId;
  const anchor = useRef<Anchor | null>(null);
  const settled = useRef<Settled | null>(null);

  useEffect(
    () =>
      onBeforeDrawerInsetChange(() => {
        const container = containerRef.current;
        const card = container ? pickAnchor(container, lastOpenId.current) : null;
        if (!card) {
          anchor.current = null;
          return;
        }
        const id = card.dataset.videoId ?? '';
        // Серия перекладок без прокрутки пользователем (протяжка ручки, открыл — закрыл)
        // целится в одну высоту: иначе остаток округления копился бы от шага к шагу.
        const previous = settled.current;
        const sameTarget = previous?.id === id && previous.scrollY === window.scrollY;
        anchor.current = {
          id,
          top: sameTarget ? previous.top : viewportTop(card),
          at: performance.now(),
        };
      }),
    [containerRef],
  );

  return useCallback(() => {
    const saved = anchor.current;
    anchor.current = null;
    const container = containerRef.current;
    if (!saved || !container || performance.now() - saved.at > ANCHOR_TTL_MS) return;
    const card = container.querySelector<HTMLElement>(`[data-video-id="${saved.id}"]`);
    if (!card) return;
    const shift = viewportTop(card) - saved.top;
    if (shift !== 0) window.scrollBy(0, shift);
    settled.current = { id: saved.id, top: saved.top, scrollY: window.scrollY };
  }, [containerRef]);
}
